import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { API, resolveImg } from '../lib/img';

const TEAL = '#4A8294';
const GOLD = '#C9A24B';
const BRIGHT_GOLD = '#E3C46A';
const BRONZE = '#9A7B2E';
const CREAM = '#F4F1E8';
const INK = '#111111';

// Fallback used only if the API has not returned yet.
const DEFAULT_FORMATS = [
  { id: 'kindle', title: 'Kindle Edition', price: '$14.99 AUD', url: 'https://www.amazon.com.au/dp/B0H87HN7BF', buttonText: 'Buy on Amazon', caption: 'Delivered instantly to your device.', available: true },
  { id: 'paperback', title: 'Paperback', price: '$35.95 AUD', url: 'https://www.amazon.com.au/dp/1764620429', buttonText: 'Buy on Amazon', caption: 'Ships from Amazon.', available: true },
  { id: 'hardback', title: 'Hardback', price: '$46.95 AUD', url: 'https://www.amazon.com.au/dp/1764620402', buttonText: 'Buy on Amazon', caption: 'Ships from Amazon.', available: true }
];

const DEFAULT_HERO_TITLE = 'Get the Book';
const DEFAULT_HERO_TEXT = "Her technology team said they were on fire. Her product team said they were frozen solid. Both were right, and that's the problem most leaders don't know how to solve.";
const DEFAULT_RETAILER_NOTE = 'More retailers coming soon.';

const BuyBook = () => {
  const [subEmail, setSubEmail] = useState('');
  const [subFirstName, setSubFirstName] = useState('');
  const [subSubmitted, setSubSubmitted] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const [subError, setSubError] = useState('');
  const [formats, setFormats] = useState(DEFAULT_FORMATS);
  const [retailers, setRetailers] = useState([]);
  const [bookCover, setBookCover] = useState('https://customer-assets.emergentagent.com/job_balanced-change/artifacts/ljo556l7_4eea1690-43b4-468c-a2dd-3248c467d977.png');
  const [heroTitle, setHeroTitle] = useState(DEFAULT_HERO_TITLE);
  const [heroText, setHeroText] = useState(DEFAULT_HERO_TEXT);
  const [retailerNote, setRetailerNote] = useState(DEFAULT_RETAILER_NOTE);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${API}/api/settings/book-cover`)
      .then((r) => r.json())
      .then((d) => { if (d && d.book_cover_url) setBookCover(d.book_cover_url); })
      .catch(() => {});
    fetch(`${API}/api/book-formats`)
      .then((r) => r.json())
      .then((d) => { if (d && d.formats && d.formats.length) setFormats(d.formats); })
      .catch(() => {});
    fetch(`${API}/api/retailers`)
      .then((r) => r.json())
      .then((d) => { if (d && Array.isArray(d.retailers)) setRetailers(d.retailers); })
      .catch(() => {});
    fetch(`${API}/api/content/buy_book`)
      .then((r) => r.json())
      .then((d) => {
        const c = (d && d.content) || {};
        if (c.hero_title) setHeroTitle(c.hero_title);
        if (c.hero_text) setHeroText(c.hero_text);
        if (c.retailer_note) setRetailerNote(c.retailer_note);
      })
      .catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubLoading(true);
    setSubError('');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: subFirstName, email: subEmail, source: 'preorder' })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSubSubmitted(true);
        setSubEmail('');
        setSubFirstName('');
      } else {
        setSubError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setSubError('Failed to connect. Please try again later.');
      console.error('Newsletter signup error:', err);
    } finally {
      setSubLoading(false);
    }
  };

  return (
    <div>
      <style>{`
        @media (max-width: 860px) {
          .preorder-hero-grid { grid-template-columns: 1fr !important; gap: 40px !important; text-align: center; }
          .preorder-hero-grid .preorder-hero-actions { justify-content: center; }
          .preorder-formats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* SECTION 1: Hero */}
      <section style={{ background: '#FFFFFF', padding: '48px 40px 80px' }} data-testid="preorder-hero">
        <div
          className="preorder-hero-grid"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: '1.1fr 0.9fr',
            gap: '64px',
            alignItems: 'center'
          }}
        >
          <div>
            <h1
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                fontWeight: '600',
                marginBottom: '28px',
                color: INK,
                lineHeight: '1.1'
              }}
              data-testid="preorder-hero-title"
            >
              {heroTitle}
            </h1>
            <p style={{ fontSize: '1.2rem', lineHeight: '1.8', color: '#333333', marginBottom: '28px', maxWidth: '560px' }} data-testid="preorder-hero-text">
              {heroText}
            </p>
            <div
              className="preorder-hero-actions"
              style={{ display: 'flex' }}
            >
              <p
                style={{
                  display: 'inline-block',
                  fontFamily: 'var(--font-display)',
                  fontSize: '1.05rem',
                  fontWeight: '600',
                  color: BRONZE,
                  backgroundColor: CREAM,
                  borderLeft: `4px solid ${GOLD}`,
                  padding: '14px 20px',
                  borderRadius: '4px',
                  margin: 0
                }}
              >
                Available now in Kindle, paperback and hardcover.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img
              src={resolveImg(bookCover)}
              alt="The Goldilocks Zone book cover"
              style={{ width: '100%', maxWidth: '360px', height: 'auto', display: 'block' }}
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: Three format cards */}
      <section className="section-container spectrum-wash">
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div
            className="preorder-formats-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '28px',
              alignItems: 'stretch',
              marginBottom: '32px'
            }}
          >
            {formats.map((f) => {
              const isActive = Boolean(f.available && f.url);
              return (
                <div
                  key={f.id}
                  data-testid={`format-card-${f.id}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    padding: '40px 28px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E7E0D4',
                    borderRadius: '10px',
                    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.06)'
                  }}
                >
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: '600', color: INK, marginBottom: '10px' }}>
                    {f.title}
                  </h3>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', fontWeight: '600', color: BRONZE, marginBottom: '28px' }}>
                    {f.price}
                  </div>

                  <div style={{ marginTop: 'auto', width: '100%' }}>
                    {isActive ? (
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`${f.id}-preorder-button`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '15px 24px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: INK,
                          backgroundColor: GOLD,
                          border: 'none',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          transition: 'background-color 0.25s ease, transform 0.25s ease'
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRIGHT_GOLD; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = GOLD; e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        Buy on Amazon <ExternalLink size={17} />
                      </a>
                    ) : (                      <button
                        disabled
                        data-testid={`${f.id}-preorder-button`}
                        style={{
                          width: '100%',
                          boxSizing: 'border-box',
                          padding: '15px 24px',
                          fontSize: '1rem',
                          fontWeight: '600',
                          color: CREAM,
                          backgroundColor: TEAL,
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'not-allowed',
                          opacity: 0.85
                        }}
                      >
                        {f.buttonText}
                      </button>
                    )}
                    <p style={{ fontSize: '0.85rem', color: '#6B6760', marginTop: '12px', lineHeight: '1.5' }}>
                      {f.caption}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {retailers.filter((r) => r.available).length > 0 && (
            <div style={{ marginTop: '64px' }} data-testid="other-retailers-section">
              <h2 style={{ textAlign: 'center', fontFamily: 'var(--font-display)', fontSize: '1.6rem', fontWeight: '600', color: INK, marginBottom: '8px' }}>
                Also available from other retailers
              </h2>
              <p style={{ textAlign: 'center', fontSize: '0.98rem', color: '#6B6760', maxWidth: '560px', margin: '0 auto 36px' }}>
                Prefer to shop elsewhere? These stores stock The Goldilocks Zone too.
              </p>
              <div
                className="preorder-formats-grid"
                style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(retailers.filter((r) => r.available).length, 3)}, minmax(0, 1fr))`, gap: '28px', alignItems: 'stretch', justifyContent: 'center', maxWidth: '760px', margin: '0 auto' }}
              >
                {retailers.filter((r) => r.available).map((r) => (
                  <div
                    key={r.id}
                    data-testid={`retailer-card-${r.id}`}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '40px 28px', backgroundColor: '#FFFFFF', border: '1px solid #E7E0D4', borderRadius: '10px', boxShadow: '0 6px 24px rgba(0, 0, 0, 0.06)' }}
                  >
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: '600', color: INK, marginBottom: '18px' }}>
                      {r.title}
                    </h3>
                    {(r.prices || []).length > 0 && (
                      <div style={{ marginBottom: '28px', width: '100%' }}>
                        {(r.prices || []).map((p, i) => (
                          <div key={i} style={{ display: 'flex', justifyContent: 'center', alignItems: 'baseline', gap: '10px', marginBottom: '6px' }}>
                            {p.label && <span style={{ fontSize: '0.95rem', color: '#6B6760' }}>{p.label}</span>}
                            <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: '600', color: BRONZE }}>{p.amount}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: 'auto', width: '100%' }}>
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`retailer-${r.id}-button`}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', boxSizing: 'border-box', padding: '15px 24px', fontSize: '1rem', fontWeight: '600', color: INK, backgroundColor: GOLD, border: 'none', borderRadius: '6px', textDecoration: 'none', cursor: 'pointer', transition: 'background-color 0.25s ease, transform 0.25s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRIGHT_GOLD; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = GOLD; e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        {r.buttonText} <ExternalLink size={17} />
                      </a>
                      {r.caption && (
                        <p style={{ fontSize: '0.85rem', color: '#6B6760', marginTop: '12px', lineHeight: '1.5' }}>
                          {r.caption}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p style={{ textAlign: 'center', fontSize: '0.98rem', color: '#6B6760', fontStyle: 'italic', maxWidth: '640px', margin: '48px auto 0' }} data-testid="preorder-retailer-note">
            {retailerNote}
          </p>
        </div>
      </section>

      {/* SECTION 3: Bulk and speaking */}
      <section style={{ backgroundColor: '#FFFFFF', padding: '100px 40px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="section-title" style={{ marginBottom: '24px' }}>
            Bulk orders, speaking and workshops
          </h2>
          <p style={{ fontSize: '1.15rem', lineHeight: '1.85', color: '#333333', marginBottom: '36px' }}>
            Putting The Goldilocks Zone in front of a leadership team, a board or a cohort? Bulk orders of ten or more copies are handled directly, with signed copies available on request. Inviting the authors to speak or running a workshop, tell us what you have in mind and we will come back to you within two business days.
          </p>
          <Link
            to="/contact"
            data-testid="preorder-contact-cta"
            className="btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '16px 32px', fontSize: '1rem', fontWeight: '600', textDecoration: 'none' }}
          >
            Get in touch <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* SECTION 4: Subscribe (dark band) */}
      <section style={{ backgroundColor: INK, padding: '100px 40px', color: CREAM }} data-testid="preorder-subscribe">
        <div style={{ maxWidth: '620px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 2.5rem)', fontWeight: '600', color: CREAM, marginBottom: '20px' }}>
            Stay in the loop
          </h2>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'rgba(244, 241, 232, 0.9)', marginBottom: '36px' }}>
            Launch events, speaking dates, and what we learn as the book makes its way into the world. No noise, just the occasional update worth your time.
          </p>

          {subSubmitted ? (
            <div
              data-testid="preorder-subscribe-success"
              style={{ padding: '18px', backgroundColor: 'rgba(201, 162, 75, 0.18)', border: `1px solid ${GOLD}`, borderRadius: '6px', color: CREAM, fontSize: '1.05rem' }}
            >
              Thank you, you are on the list.
            </div>
          ) : (
            <form
              onSubmit={handleSubscribe}
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}
            >
              <input
                type="text"
                required
                value={subFirstName}
                onChange={(e) => setSubFirstName(e.target.value)}
                placeholder="First name"
                data-testid="preorder-subscribe-firstname"
                style={{
                  flex: '1 1 260px',
                  padding: '16px 18px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
              <input
                type="email"
                required
                value={subEmail}
                onChange={(e) => setSubEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="preorder-subscribe-input"
                style={{
                  flex: '1 1 260px',
                  padding: '16px 18px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="submit"
                disabled={subLoading}
                data-testid="preorder-subscribe-button"
                style={{
                  flex: '0 0 auto',
                  padding: '16px 28px',
                  backgroundColor: subLoading ? '#999999' : GOLD,
                  color: INK,
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: subLoading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.25s ease'
                }}
                onMouseEnter={(e) => { if (!subLoading) e.currentTarget.style.backgroundColor = BRIGHT_GOLD; }}
                onMouseLeave={(e) => { if (!subLoading) e.currentTarget.style.backgroundColor = GOLD; }}
              >
                {subLoading ? 'Submitting...' : 'Keep me posted'}
              </button>
            </form>
          )}

          {!subSubmitted && subError && (
            <p data-testid="preorder-subscribe-error" style={{ marginTop: '14px', color: BRIGHT_GOLD, fontSize: '0.9rem' }}>
              {subError}
            </p>
          )}

          <p style={{ fontSize: '0.85rem', color: 'rgba(244, 241, 232, 0.65)', marginTop: '16px' }}>
            We only use your email for book and event updates.
          </p>
          <p style={{ fontSize: '0.8rem', color: 'rgba(244, 241, 232, 0.55)', marginTop: '28px', lineHeight: '1.6' }}>
            The book is available now. Subscribers hear about upcoming events first.
          </p>
        </div>
      </section>
    </div>
  );
};

export default BuyBook;
